import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Loader2, Users, AlertTriangle, Filter, Layers, Download } from "lucide-react";

const SEG_LABEL = {
  new_users: "New (7d)", trial_users: "Trial", paying_users: "Paying",
  monthly_subscribers: "Monthly", annual_subscribers: "Annual", starter: "Starter",
  growth: "Growth", enterprise: "Enterprise", past_due: "Past due", canceled: "Canceled",
  power_users: "Power users", high_ai_usage: "High AI usage", upgrade_ready: "Upgrade ready",
  churn_risk: "Churn risk", highly_engaged: "Highly engaged", low_engagement: "Low engagement",
};
const RISK_STYLE = { high: "text-red-400", medium: "text-amber-400", low: "text-emerald-400" };

function Funnel({ title, steps }) {
  return (
    <div className="rounded-xl border border-white/8 bg-[#121214] p-4" data-testid={`funnel-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-xs text-[#71717A] mb-3">{title}</div>
      {steps.map((s, i) => (
        <div key={s.step} className="mb-2 last:mb-0">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-[#D4D4D8]">{s.step}</span>
            <span className="text-[#A1A1AA]">{s.count} · {s.conversion}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full fv-gold-gradient" style={{ width: `${i === 0 ? 100 : s.conversion}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminIntelligence() {
  const [ov, setOv] = useState(null);
  const [churn, setChurn] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/intelligence/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/admin/intelligence/churn").then((r) => setChurn(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportData = (dataset, format) => {
    const url = `${api.defaults.baseURL}/admin/intelligence/export?dataset=${dataset}&format=${format}`;
    window.open(url, "_blank");
  };

  if (!ov || !churn) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-intelligence">
      {/* Segments */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-medium flex items-center gap-2"><Layers className="w-4 h-4 text-[#D4AF37]" /> Customer segments</h3>
          <div className="flex gap-2">
            {["csv", "xlsx", "pdf"].map((f) => (
              <button key={f} onClick={() => exportData("segments", f)} data-testid={`export-segments-${f}`}
                className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-[#A1A1AA] hover:text-white flex items-center gap-1">
                <Download className="w-3 h-3" /> {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {Object.entries(ov.segments).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/8 bg-[#121214] px-3 py-2.5" data-testid={`segment-${k}`}>
              <div className="text-lg font-heading text-white">{v}</div>
              <div className="text-[10px] text-[#71717A]">{SEG_LABEL[k] || k}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-3 text-xs text-[#71717A]">
          <span>DAU: <span className="text-white">{ov.active_users.dau}</span></span>
          <span>WAU: <span className="text-white">{ov.active_users.wau}</span></span>
        </div>
      </div>

      {/* Funnels */}
      <div>
        <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Filter className="w-4 h-4 text-[#D4AF37]" /> Conversion funnels</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Funnel title="Registration" steps={ov.funnels.registration} />
          <Funnel title="Trial" steps={ov.funnels.trial} />
          <Funnel title="Upgrade" steps={ov.funnels.upgrade} />
          <Funnel title="Cancellation" steps={ov.funnels.cancellation} />
        </div>
      </div>

      {/* Churn + modules */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#D4AF37]" /> Churn risk</h3>
            <div className="flex gap-2">
              {["csv", "xlsx", "pdf"].map((f) => (
                <button key={f} onClick={() => exportData("churn", f)} data-testid={`export-churn-${f}`}
                  className="text-xs px-2 py-0.5 rounded-full border border-white/10 text-[#A1A1AA] hover:text-white">{f.toUpperCase()}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-4 mb-3 text-sm">
            {["high", "medium", "low"].map((b) => (
              <span key={b} className={RISK_STYLE[b]} data-testid={`churn-bucket-${b}`}>{b}: {churn.buckets[b]}</span>
            ))}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {churn.at_risk.map((r) => (
              <div key={r.user_id} className="flex items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0" data-testid="churn-row">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{r.email}</div>
                  <div className="text-[11px] text-[#71717A] truncate">{r.recommended_actions[0]}</div>
                </div>
                <span className={`text-xs shrink-0 ${RISK_STYLE[r.risk_bucket]}`}>{r.churn_risk}</span>
              </div>
            ))}
            {churn.at_risk.length === 0 && <p className="text-sm text-[#52525B]">No at-risk customers.</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium flex items-center gap-2"><Users className="w-4 h-4 text-[#D4AF37]" /> Module usage</h3>
            {["csv", "xlsx", "pdf"].map((f) => (
              <button key={f} onClick={() => exportData("modules", f)} data-testid={`export-modules-${f}`}
                className="text-xs px-2 py-0.5 rounded-full border border-white/10 text-[#A1A1AA] hover:text-white ml-1">{f.toUpperCase()}</button>
            ))}
          </div>
          {ov.module_usage.all.map((m) => (
            <div key={m.module} className="flex justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
              <span className="text-[#D4D4D8]">{m.label}</span><span className="text-white">{m.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cohort retention */}
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="cohort-retention">
        <h3 className="text-white font-medium mb-3">Cohort retention</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {ov.cohorts.map((c) => (
            <div key={c.cohort} className="rounded-xl border border-white/8 bg-[#0d0d0f] px-3 py-2.5">
              <div className="text-xs text-[#71717A]">{c.cohort}</div>
              <div className="text-lg font-heading text-[#D4AF37]">{c.retention_pct}%</div>
              <div className="text-[10px] text-[#52525B]">{c.retained}/{c.size}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
